<!--@format-->
<template>
    <div class="magic-branding-fonts-settings-widget">
        <div class="widget-header">
            <h3>{{ title }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <p>You can set a font exception for this specific heading.</p>
            <div class="field-container">
                <span class="field-title">Font</span>
                <div class="field-details">
                    <app-font-select
                        :custom-font-sizes="fontSizes"
                        :font-family="currentValue.fontFamily"
                        :is-size-editable="false"
                        @select:font-family="handleSelectedFontFamily"
                    />
                    <app-button
                        v-if="hasFontFamilyChanged"
                        @click="handleClickRevert('fontFamily')"
                    >
                        <e7-reply-svg />
                    </app-button>
                </div>
            </div>
            <div class="field-container">
                <span class="field-title">Font Size</span>
                <div class="field-details">
                    <app-font-select
                        :custom-font-sizes="fontSizes"
                        :font-size="currentValue.fontSize"
                        :is-family-editable="false"
                        @select:font-size="handleSelectedFontSize"
                    />
                    <app-button v-if="hasFontSizeChanged" @click="handleClickRevert('fontSize')">
                        <e7-reply-svg />
                    </app-button>
                </div>
            </div>
            <div class="field-container">
                <span class="field-title">Line Height</span>
                <div class="field-details">
                    <div class="slider-container">
                        <div class="slider-label">
                            <span>Percentage</span>
                            <span class="slider-value"> {{ currentValue.lineHeight }}%</span>
                        </div>
                        <app-range-slider
                            v-model="currentValue.lineHeight"
                            fill-color="#382153"
                            :config="{ min: 100, max: 300, step: 1 }"
                        />
                    </div>
                    <app-button
                        v-if="hasLineHeightChanged"
                        @click="handleClickRevert('lineHeight')"
                    >
                        <e7-reply-svg />
                    </app-button>
                </div>
            </div>
            <div class="field-container">
                <span class="field-title">Font Preview</span>
                <div class="font-preview-container">
                    <div
                        v-for="(option, idx) in previewOptions"
                        :key="idx"
                        class="font-preview"
                        :style="getFontBackground(option)"
                    >
                        <p :style="fontPreviewStyles">
                            Combine Customer Satisfaction (CSAT) and Net Promoter Score (NPS) to get
                            a 360-degree understanding of your customer sentiment, nurture business
                            relationships, and streamline processes.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <app-button class="transparent-btn" @click="closeWidget">Close</app-button>
            <app-button @click="handleSaveAndClose"> Save & Close </app-button>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';

const AppButton = () => import('@/components/common/app-button');
const AppFontSelect = () => import('@/components/common/app-font-select');
const AppRangeSlider = () => import('@/components/common/app-range-slider');
const E7ReplyySvg = () => import('@/components/svg/grid/e7-reply-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

export default {
    name: 'magic-branding-fonts-settings-widget',

    components: {
        'app-button': AppButton,
        'app-font-select': AppFontSelect,
        'app-range-slider': AppRangeSlider,
        'e7-reply-svg': E7ReplyySvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            currentValue: {},

            fontSizes: [
                '8',
                '9',
                '10',
                '11',
                '12',
                '13',
                '14',
                '15',
                '16',
                '17',
                '18',
                '19',
                '20',
                '22',
                '24',
                '26',
                '28',
                '30',
                '32',
                '34',
                '36',
                '40',
                '44',
                '48',
                '52',
                '56',
                '64',
                '72',
            ],

            previewOptions: ['light', 'dark'],
        };
    },

    computed: {
        title() {
            const { options } = this.widget;
            return options ? options.title : 'Font settings';
        },

        initialValue() {
            const { options } = this.widget;
            return options.font;
        },

        hasFontFamilyChanged() {
            return this.initialValue.fontFamily.key !== this.currentValue.fontFamily.key;
        },

        hasFontSizeChanged() {
            return this.initialValue.fontSize !== this.currentValue.fontSize;
        },

        hasLineHeightChanged() {
            return this.initialValue.lineHeight !== this.currentValue.lineHeight;
        },

        fontPreviewStyles() {
            return {
                color: '#EE206F',
                'font-family': this.currentValue.fontFamily.value,
                'font-size': this.currentValue.fontSize + 'px',
                'font-weight': this.currentValue.branding === 'headings' ? 'bold' : 'normal',
                'line-height': this.currentValue.lineHeight + '%',
            };
        },
    },

    created() {
        this.currentValue = { ...this.initialValue };
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSaveAndClose() {
            const { options } = this.widget;
            EventBus.$emit('font:settings-selected', {
                value: this.currentValue,
                property: options.property,
                subproperty: options.subproperty,
            });
            this.closeWidget();
        },

        handleSelectedFontFamily(value) {
            this.currentValue.fontFamily = value;
            this.currentValue.custom = true;
        },

        handleSelectedFontSize(value) {
            this.currentValue.fontSize = parseInt(value);
        },

        handleClickRevert(prop) {
            this.currentValue[prop] = this.initialValue[prop];
            if (prop === 'fontFamily') {
                this.currentValue.custom = this.initialValue.custom;
            }
        },

        getFontBackground(option) {
            if (option === 'dark') {
                return {
                    'background-color': '#373E4D',
                };
            }
        },
    },
};
</script>

<style>
.magic-branding-fonts-settings-widget .widget-header {
    text-transform: capitalize;
}

.magic-branding-fonts-settings-widget .field-container {
    display: flex;
    flex-direction: column;
    gap: 13px;
    margin: 20px 0 16px;
}

.magic-branding-fonts-settings-widget .field-container .field-title {
    color: #382153;
    font-weight: 500;
}

.magic-branding-fonts-settings-widget .field-container .field-details {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 20px;
}

.magic-branding-fonts-settings-widget .field-container .field-details .app-font-select,
.magic-branding-fonts-settings-widget .field-container .field-details .slider-container {
    align-items: center;
    background-color: #e9e9ef;
    border-radius: 16px;
    border: 1px solid #cac6cb;
    box-shadow: inset -4px 3px 6px #8274e833;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px;
    width: 296px;
}

.magic-branding-fonts-settings-widget
    .field-container
    .field-details
    .app-font-select
    .font-selector {
    width: 100%;
}

.magic-branding-fonts-settings-widget .slider-container .slider-label {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    width: 100%;
}

.magic-branding-fonts-settings-widget .slider-container .app-range-slider {
    width: 100%;
}

.magic-branding-fonts-settings-widget .field-container .field-details .e7-reply-svg .a {
    fill: #ffffff;
}

.magic-branding-fonts-settings-widget .font-preview-container {
    display: flex;
    gap: 16px;
    justify-content: flex-start;
}

.magic-branding-fonts-settings-widget .font-preview-container .font-preview {
    border-radius: 16px;
    box-shadow: inset 0px 3px 11px #8274e833;
    overflow: hidden;
    padding: 15px 12px 24px 24px;
    width: 279px;
}

.magic-branding-fonts-settings-widget .font-preview-container p {
    margin: 0;
}

.magic-branding-fonts-settings-widget .widget-footer .transparent-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    color: #877d88;
}

.magic-branding-fonts-settings-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}
</style>
